import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Stack, Card } from '@mui/material';
import { HeaderInfoPlaceholder } from './HeaderInfo.placeholder';
import { HeaderInfoContext } from './context';
import { UserInfo } from './components/UserInfo';
import { DeviceInfo } from './components/DeviceInfo';
import { ScoreInfo } from './components/ScoreInfo';
import { QuestionsInfo } from './components/QuestionsInfo';
import { useTestType } from 'modules/Core/Hooks';
import { CATEGORY_FULL, CATEGORY_READING, CATEGORY_LISTENING } from 'constants';

export function HeaderInfo({
  testType,
  student,
  id,
  date,
  dateLabel,
  data,
  deviceInfo,
  scoreInfo,
  categories,
  selectedCategory,
  onChangeCategory,
  isLoading,
  isCustomTest,
  sx,
  children,
}) {
  const { testCategory } = useParams();
  const { isCefr } = useTestType({ testType });

  const currentCategory = useMemo(() => {
    return categories?.find((category) => category.id === selectedCategory);
  }, [categories, selectedCategory]);

  const shouldShowQuestionsInfo = useMemo(() => {
    const isReadingOrListening = [
      CATEGORY_FULL,
      CATEGORY_READING,
      CATEGORY_LISTENING,
    ].includes(testCategory);

    return !!isCefr && !!isReadingOrListening;
  }, [isCefr, testCategory]);

  if (isLoading) {
    return <HeaderInfoPlaceholder />;
  }

  return (
    <HeaderInfoContext.Provider
      value={{
        testType,
        student,
        data,
        deviceInfo,
        scoreInfo,
        categories,
        selectedCategory,
        currentCategory,
        onChangeCategory,
        testCategory,
      }}
    >
      <Card elevation={0} sx={{ ...(sx || {}) }}>
        <UserInfo student={student} id={id} date={date} dateLabel={dateLabel} />
        <Stack
          sx={{
            p: {
              xs: 2,
              sm: 4,
            },
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
            gap: {
              xs: '3rem',
              sm: '5.6rem',
            },
          }}
        >
          {children}
        </Stack>
        {!!deviceInfo && <DeviceInfo />}
        {!!scoreInfo && <ScoreInfo isCustomTest={isCustomTest} />}
        {!!shouldShowQuestionsInfo && <QuestionsInfo />}
      </Card>
    </HeaderInfoContext.Provider>
  );
}

HeaderInfo.propTypes = {
  testType: PropTypes.string,
  student: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    email: PropTypes.string,
    avatar: PropTypes.object,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  date: PropTypes.string,
  dateLabel: PropTypes.string,
  data: PropTypes.object,
  categories: PropTypes.array,
  selectedCategory: PropTypes.any,
  onChangeCategory: PropTypes.func,
  deviceInfo: PropTypes.object,
  scoreInfo: PropTypes.array,
  isLoading: PropTypes.bool,
  isCustomTest: PropTypes.bool,
  sx: PropTypes.object,
  children: PropTypes.node,
};
